import React, { useState, useEffect, useContext } from 'react';
import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';
import { AuthContext } from '../../context/AuthContext';
import { useSidebarState } from '../../components/sidebar/SidebarStateContext';
import telimanData from '../../dataset_teliman.json';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Avatar,
  Tooltip,
  Badge
} from '@mui/material';
import { 
  Business as BusinessIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import './Licenses.scss';

const Licenses = () => {
  const { currentUser, currentService } = useContext(AuthContext);
  const { collapsed } = useSidebarState();
  const [licenses, setLicenses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [newLicense, setNewLicense] = useState({
    companyName: '',
    totalLicenses: 0,
    assignedLicenses: 0,
    accountType: 'organization',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    status: 'active',
    features: []
  });

  // Agregar una clase basada en el estado de colapso
  const containerClass = collapsed ? "listContainer collapsed" : "listContainer";

  useEffect(() => {
    loadLicensesData();
  }, []);

  const loadLicensesData = () => {
    // Simular carga de datos de licencias
    setTimeout(() => {
      const mockCompanies = [
        {
          id: 1,
          name: 'TELIMAN',
          totalLicenses: 116,
          assignedLicenses: 116,
          accountType: 'enterprise',
          status: 'active',
          startDate: '2025-01-01',
          endDate: '2026-01-01',
          features: ['vigilantes', 'tiendas', 'documentos', 'reportes', 'analytics', 'api', 'mobile', 'backup'],
          color: '#ff6b35'
        }
      ];

      // Cargar usuarios de TELIMAN desde el dataset
      const telimanUsers = telimanData.usuarios || [];
      
      setCompanies(mockCompanies);
      setLicenses(mockCompanies);
      setUsers(telimanUsers);
      setLoading(false);
    }, 1500);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trial':
        return 'warning';
      case 'expired':
        return 'error';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'trial':
        return <WarningIcon color="warning" />;
      case 'expired':
        return <ErrorIcon color="error" />;
      case 'suspended':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getAccountTypeLabel = (type) => {
    switch (type) {
      case 'basic':
        return 'Básico';
      case 'organization':
        return 'Organización';
      case 'enterprise':
        return 'Empresa';
      default:
        return type;
    }
  };

  const getAccountTypeColor = (type) => {
    switch (type) {
      case 'basic':
        return 'default';
      case 'organization':
        return 'primary';
      case 'enterprise':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (license = null) => {
    setSelectedLicense(license);
    if (license) {
      setNewLicense(license);
    } else {
      setNewLicense({
        companyName: '',
        totalLicenses: 0,
        assignedLicenses: 0,
        accountType: 'organization',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        status: 'active',
        features: []
      });
    }
    setOpenDialog(true);
  };

  const handleSaveLicense = () => {
    if (selectedLicense) {
      // Editar licencia existente
      setLicenses(licenses.map(license => 
        license.id === selectedLicense.id ? { ...newLicense, id: selectedLicense.id } : license
      ));
    } else {
      // Crear nueva licencia
      const license = {
        ...newLicense,
        id: Date.now(),
        color: '#1976d2'
      };
      setLicenses([...licenses, license]);
    }
    setOpenDialog(false);
    setSelectedLicense(null);
  };

  const handleDeleteLicense = () => {
    setLicenses(licenses.filter(license => license.id !== selectedLicense.id));
    setOpenDialog(false);
    setSelectedLicense(null);
  };

  const filteredLicenses = licenses.filter(license => {
    const matchesSearch = license.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || license.accountType === filterType;
    return matchesSearch && matchesFilter;
  });

  const getUsagePercentage = (assigned, total) => {
    return total > 0 ? (assigned / total) * 100 : 0;
  };

  const TabPanel = ({ children, value, index, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`license-tabpanel-${index}`}
        aria-labelledby={`license-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            {children}
          </Box>
        )}
      </div>
    );
  };

  return (
    <div className="list">
      <Sidebar />
      <div className={containerClass}>
        <Navbar />
        
        <div className="licenses-content">
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            aria-label="breadcrumb"
            className="breadcrumbs"
          >
            <Link 
              underline="hover" 
              color="inherit" 
              href="/" 
              className="breadcrumb-link"
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Inicio
            </Link>
            <Typography color="text.primary" className="breadcrumb-current">
              Licencias
            </Typography>
          </Breadcrumbs>

          {/* Page Header */}
          <div className="page-header">
            <Typography variant="h4" component="h1" className="page-title">
              <SecurityIcon className="title-icon" />
              Licencias
            </Typography>
            <Typography variant="body1" color="textSecondary" className="page-subtitle">
              Gestión de licencias y suscripciones para empresas
            </Typography>
          </div>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="license tabs">
              <Tab label="Suscripciones" />
              <Tab label="Usuarios ({users.length})" />
              <Tab label="Solicitudes" />
              <Tab label="Directiva de notificación automática" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <TabPanel value={tabValue} index={0}>
            {/* Informational Text */}
            <Card className="info-card">
              <CardContent>
                <Typography variant="body1" paragraph>
                  Seleccione una empresa para ver y asignar licencias. Cada empresa que se muestra a continuación 
                  puede contener licencias de varias suscripciones. 
                  <Link href="#" className="info-link">
                    Más información sobre la asignación de licencias
                  </Link>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <Link href="#" className="info-link">
                    Ir a los productos para administrar la facturación o comprar más licencias.
                  </Link>
                </Typography>
              </CardContent>
            </Card>

            {/* Actions Bar */}
            <Card className="actions-card">
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Buscar empresas..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        size="small"
                      >
                        Exportar
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        size="small"
                        onClick={loadLicensesData}
                      >
                        Actualizar
                      </Button>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Filtrar</InputLabel>
                        <Select
                          value={filterType}
                          label="Filtrar"
                          onChange={(e) => setFilterType(e.target.value)}
                        >
                          <MenuItem value="all">Todos</MenuItem>
                          <MenuItem value="basic">Básico</MenuItem>
                          <MenuItem value="organization">Organización</MenuItem>
                          <MenuItem value="enterprise">Empresa</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        size="small"
                        onClick={() => handleOpenDialog()}
                      >
                        Nueva Licencia
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Licenses Table */}
            <Card className="licenses-table-card">
              <CardContent sx={{ padding: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre</TableCell>
                        <TableCell align="center">Licencias Disponibles</TableCell>
                        <TableCell align="center">Licencias Asignadas</TableCell>
                        <TableCell align="center">Tipo de Cuenta</TableCell>
                        <TableCell align="center">Estado</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLicenses.map((license) => (
                        <TableRow key={license.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Avatar 
                                sx={{ 
                                  bgcolor: license.color,
                                  width: 40,
                                  height: 40
                                }}
                              >
                                {license.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {license.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {license.features.length} características
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="h6" color="primary">
                              {license.totalLicenses - license.assignedLicenses}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                              <Typography variant="body2">
                                {license.assignedLicenses}/{license.totalLicenses}
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={getUsagePercentage(license.assignedLicenses, license.totalLicenses)}
                                sx={{ width: '100%', height: 8, borderRadius: 4 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={getAccountTypeLabel(license.accountType)}
                              color={getAccountTypeColor(license.accountType)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                              {getStatusIcon(license.status)}
                              <Chip
                                label={license.status}
                                color={getStatusColor(license.status)}
                                size="small"
                                variant="filled"
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={1}>
                              <Tooltip title="Ver detalles">
                                <IconButton size="small" onClick={() => handleOpenDialog(license)}>
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar">
                                <IconButton size="small" onClick={() => handleOpenDialog(license)}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton size="small" color="error" onClick={() => handleOpenDialog(license)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Card className="users-table-card">
              <CardContent sx={{ padding: 0 }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)', minHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="center">Servicios</TableCell>
                        <TableCell align="center">Clientes</TableCell>
                        <TableCell align="center">Logins</TableCell>
                        <TableCell align="center">Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Avatar sx={{ bgcolor: user.activo ? '#4caf50' : '#9e9e9e' }}>
                                {user.persona?.nombre?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {user.persona?.nombre} {user.persona?.apellido1} {user.persona?.apellido2}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  TIP: {user.tipvs}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{user.email}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={user.servicios?.length || 0} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={user.clientes?.length || 0} 
                              size="small" 
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {user.estadisticas?.total_logins || 0}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={user.activo ? 'Activo' : 'Inactivo'}
                              color={user.activo ? 'success' : 'default'}
                              size="small"
                              variant="filled"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="textSecondary">
                    Total: {users.length} usuarios
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Solicitudes de Licencias
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Aquí se mostrarán las solicitudes pendientes de nuevas licencias.
                </Typography>
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Directiva de Notificación Automática
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Configuración de notificaciones automáticas para licencias.
                </Typography>
              </CardContent>
            </Card>
          </TabPanel>

          {/* Dialog para crear/editar licencia */}
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              {selectedLicense ? 'Editar Licencia' : 'Nueva Licencia'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ marginTop: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre de la Empresa"
                    value={newLicense.companyName}
                    onChange={(e) => setNewLicense({ ...newLicense, companyName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Cuenta</InputLabel>
                    <Select
                      value={newLicense.accountType}
                      label="Tipo de Cuenta"
                      onChange={(e) => setNewLicense({ ...newLicense, accountType: e.target.value })}
                    >
                      <MenuItem value="basic">Básico</MenuItem>
                      <MenuItem value="organization">Organización</MenuItem>
                      <MenuItem value="enterprise">Empresa</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Total de Licencias"
                    type="number"
                    value={newLicense.totalLicenses}
                    onChange={(e) => setNewLicense({ ...newLicense, totalLicenses: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Licencias Asignadas"
                    type="number"
                    value={newLicense.assignedLicenses}
                    onChange={(e) => setNewLicense({ ...newLicense, assignedLicenses: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Inicio"
                    type="date"
                    value={newLicense.startDate}
                    onChange={(e) => setNewLicense({ ...newLicense, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Fin"
                    type="date"
                    value={newLicense.endDate}
                    onChange={(e) => setNewLicense({ ...newLicense, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={newLicense.status}
                      label="Estado"
                      onChange={(e) => setNewLicense({ ...newLicense, status: e.target.value })}
                    >
                      <MenuItem value="active">Activo</MenuItem>
                      <MenuItem value="trial">Prueba</MenuItem>
                      <MenuItem value="expired">Expirado</MenuItem>
                      <MenuItem value="suspended">Suspendido</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {selectedLicense && (
                <Button onClick={handleDeleteLicense} color="error">
                  <DeleteIcon /> Eliminar
                </Button>
              )}
              <Button onClick={() => setOpenDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveLicense} variant="contained">
                {selectedLicense ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Licenses;